import {
    AddressLines,
    DangerousGoodsIndicator,
    HandlingUnitCountByFreightType,
    MoreAddressesLabel,
    PartialConfirmationWarning,
    TimeWindowComponent,
    TransportOrderActionsDropdown,
    TransportOrderNumbersCell,
    TransportOrderStatusOrAcceptBadge,
    TransportAndCargoType,
    VolumesByFreightType,
    WeightsByFreightType,
} from "../orderOverviewSubcomponents"
import { sortShipmentByDueDate } from "@/helper/shipmentHelper"
import {
    TransportOrder,
    TransportOrderType
} from "@/model/transportorder/transportOrder"
import {
    summarizeMeasurements,
    TransportOrderMeasurementSummary
} from "@/model/transportorder/transportOrderMeasurements"
import { formatDateColumn } from "@/helper/formatHelper"
import { ReactNode } from "react"
import {
    isAnyShipmentConfirmed,
    isTransportOrderPartiallyConfirmed
} from "@/model/transportorder/transportOrderEditing"
import Tooltip from "@rio-cloud/rio-uikit/Tooltip"
import { FormattedMessage } from "react-intl"
import OverlayTrigger from "@rio-cloud/rio-uikit/OverlayTrigger"
import { useAppDispatch } from "@/redux/store"
import { transportationReportSlice } from "@/redux/transportation-report.slice"
import { transportOrderToTransportationReport } from "./transportOrderToTransportationReport"
import { OverflowClipboardCell } from "./OverflowClipboardCell"
import {
    getEarliestLoadingTimeWindow,
    getLatestTransportChainDeliveryLocation,
    getEarliestTransportChainPickupLocation,
    getTableShipments,
} from "./transportOrderTableData"

export const TransportOrderTableRow = (
    props: {
        transportOrder: TransportOrder,
        onDetailsClicked: (_: TransportOrder) => void,
        onAcceptClicked: (_: TransportOrder) => Promise<void>,
        rowRef?: React.Ref<HTMLTableRowElement>,
        isScrollLoadTriggerRow?: boolean,
    }
): ReactNode => {

    const { transportOrder, onDetailsClicked, onAcceptClicked, rowRef, isScrollLoadTriggerRow } = props

    const shipments = transportOrder._embedded?.shipments || []
    const sortedShipments = sortShipmentByDueDate(shipments)
    const tableShipments = getTableShipments(sortedShipments)

    const pickupLocation = getEarliestTransportChainPickupLocation(tableShipments)
    const deliveryLocation = getLatestTransportChainDeliveryLocation(tableShipments)

    const accepted = isAnyShipmentConfirmed(transportOrder)
    const atLeastOneTimeSlotIdIsMissing = transportOrder._embedded?.shipments.some(
        shipment => shipment.confirmationDraft.accessCodeAtUnloading === undefined
    )
    const atLeastOneAssetAssignmentIsMissing = transportOrder._embedded?.shipments.some(
        shipment => {
            const asset = shipment.confirmationDraft.asset
            return asset === undefined || (asset.id === undefined && asset.licensePlate === undefined)
        }
    )
    const isMaterialTransport = transportOrder.orderType === TransportOrderType.MATERIAL
    const hasWarning = isMaterialTransport && accepted && (atLeastOneTimeSlotIdIsMissing || atLeastOneAssetAssignmentIsMissing)
    const hasRowWarning = hasWarning || isTransportOrderPartiallyConfirmed(transportOrder)

    const measurementSummary: TransportOrderMeasurementSummary = summarizeMeasurements(tableShipments)

    const dispatch = useAppDispatch()
    const agk = transportOrder.external_bundle_id ?? ""

    const showDialog = () => {
        dispatch(transportationReportSlice.actions.setDialogInitialValues(transportOrderToTransportationReport(transportOrder)))
        dispatch(transportationReportSlice.actions.setDialogOpen(true))
    }

    return (
        <tr
            ref={rowRef}
            data-testid={`transportOrderId_${transportOrder.id}`}
            data-scroll-load-trigger-row={isScrollLoadTriggerRow ? "true" : undefined}
            className={`${transportOrder.id} ${hasRowWarning ? "warning" : ""}`}
        >
            <td>
                <div className="space-y-10">
                    <PartialConfirmationWarning transportOrder={transportOrder}/>
                    {hasWarning && atLeastOneTimeSlotIdIsMissing &&
                        <OverlayTrigger
                            placement={"right"}
                            overlay={
                                <Tooltip id="tooltip" allowOnTouch width={300} textAlignment={"left"}>
                                    <FormattedMessage id="transportOrder.tooltip.timeslotBookingIdMissing"/>
                                </Tooltip>
                            }
                        >
                            <span data-testid={"TimeBookingWarningIcon"}
                                className="rioglyph rioglyph-time-alt text-color-warning text-size-h5"/>
                        </OverlayTrigger>
                    }
                    {hasWarning && atLeastOneAssetAssignmentIsMissing &&
                        <OverlayTrigger
                            placement={"right"}
                            overlay={
                                <Tooltip id="tooltip" allowOnTouch width={300} textAlignment={"left"}>
                                    <FormattedMessage id="transportOrder.tooltip.assetAssignmentMissing"/>
                                </Tooltip>
                            }
                        >
                            <span data-testid={"AssetAssignmentWarningIcon"}
                                className="rioglyph rioglyph-truck text-color-warning text-size-h5"/>
                        </OverlayTrigger>
                    }
                </div>
            </td>
            <td>
                {formatDateColumn(transportOrder.created_at)}
            </td>
            <td>
                {formatDateColumn(getEarliestLoadingTimeWindow(transportOrder))}
            </td>
            <td>
                <div className="display-flex flex-row">
                    <span className="text-normal ellipsis-1">{transportOrder.customer_data?.name ?? ""}</span>
                </div>
            </td>
            <td>
                <TimeWindowComponent
                    from={pickupLocation?.dateTime}
                    countryCodeFrom={pickupLocation?.dateTimeCountryCode}
                    to={deliveryLocation?.dateTime}
                    countryCodeTo={deliveryLocation?.dateTimeCountryCode}
                />
            </td>
            <td>
                <div className="display-flex flex-row no-wrap align-items-center">
                    <div className="flex-1-1" style={{ minWidth: 0 }}>
                        <AddressLines
                            title={<FormattedMessage id={"transportOrder.shipper"}/>}
                            party={pickupLocation?.party}
                            partyExternalIdentifiers={pickupLocation?.externalIdentifiers ?? {}}
                            cellId={`shipper-${transportOrder.id}`}
                            measureOverflow
                        />
                    </div>
                    <MoreAddressesLabel
                        shipments={tableShipments}
                        extractParty={(item) => item.currentTransportLeg.placeOfDeparture}
                    />
                </div>
            </td>
            <td>
                <div className="display-flex flex-row no-wrap align-items-center">
                    <div className="flex-1-1" style={{ minWidth: 0 }}>
                        <AddressLines
                            title={<FormattedMessage id={"transportOrder.receiver"}/>}
                            party={deliveryLocation?.party}
                            partyExternalIdentifiers={deliveryLocation?.externalIdentifiers ?? {}}
                            cellId={`receiver-${transportOrder.id}`}
                            measureOverflow
                        />
                    </div>
                    <MoreAddressesLabel
                        shipments={tableShipments}
                        extractParty={(item) => item.currentTransportLeg.placeOfArrival}
                    />
                </div>
            </td>
            <td>
                <OverflowClipboardCell
                    cellId={`agk-${transportOrder.id}`}
                    title="AGK"
                    value={agk}
                />
            </td>
            <td>
                <TransportOrderNumbersCell transportOrder={transportOrder}/>
            </td>
            <td>
                <div className="display-flex flex-column">
                    <TransportAndCargoType transportOrder={transportOrder} shipments={tableShipments}/>
                </div>
            </td>
            <td>
                <div className="display-flex flex-column">
                    <WeightsByFreightType summary={measurementSummary}/>
                </div>
            </td>
            <td>
                <div className="display-flex flex-column">
                    <VolumesByFreightType summary={measurementSummary}/>
                </div>
            </td>
            <td>
                <div className="display-flex flex-column">
                    <HandlingUnitCountByFreightType summary={measurementSummary}/>
                </div>
            </td>
            <td>
                <div className="display-flex flex-column">
                    <DangerousGoodsIndicator shipments={tableShipments}/>
                </div>
            </td>
            <td>
                <div className="display-flex flex-column">
                    <div>
                        <TransportOrderStatusOrAcceptBadge
                            transportOrder={transportOrder}
                            onAcceptClicked={onAcceptClicked}
                        />
                    </div>
                </div>
            </td>
            <td className="table-action">
                <span>
                    <TransportOrderActionsDropdown
                        transportOrder={transportOrder}
                        onDetailsClicked={onDetailsClicked}
                        onAcceptClicked={onAcceptClicked}
                        onFreightPaymentDetailsClicked={() => {
                            showDialog()
                        }}
                    />
                </span>
            </td>
        </tr>
    )
}
